import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ChevronDown, ChevronUp, Funnel, RefreshCw } from "lucide-react";
import { AppLayout } from "../../../components/layout";
import { ContentCard, PageHeader } from "../../../components/ui";
import { TablePagination } from "../../leave-management/components/TablePagination";
import { MovementTypeBadge } from "../components/MovementTypeBadge";
import { ReferenceLink } from "../components/ReferenceLink";
import { inventoryManagementService } from "../../../services/inventoryManagementService";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { cn } from "../../../lib/utils";

const MONTH_KEYS = [
  "dateTime.monthJan",
  "dateTime.monthFeb",
  "dateTime.monthMar",
  "dateTime.monthApr",
  "dateTime.monthMay",
  "dateTime.monthJun",
  "dateTime.monthJul",
  "dateTime.monthAug",
  "dateTime.monthSep",
  "dateTime.monthOct",
  "dateTime.monthNov",
  "dateTime.monthDec",
];

const MOVEMENT_TYPE_KEYS = {
  Receipt: "inventory.movementReceipt",
  Return: "inventory.movementReturn",
  "Transfer In": "inventory.movementTransferIn",
  "Transfer Out": "inventory.movementTransferOut",
  Reservation: "inventory.movementReservation",
  Outward: "inventory.movementOutward",
  Revocation: "inventory.movementRevocation",
  "Defect Adjustment": "inventory.movementDefectAdjustment",
};

const PRODUCT_STATUS_KEYS = {
  Active: "status.active",
  "End of Production": "inventory.endOfProduction",
  Inactive: "common.inactive",
};

const BATCH_STATUS_KEYS = {
  Available: "status.available",
  "Low Stock": "inventory.lowStock",
  Reserved: "status.reserved",
  "Expired Warranty": "inventory.expiredWarranty",
};

// Format currency helper
const formatCurrency = (val) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(val);
};

const MOVEMENT_FILTER_VALUES = [
  "Receipt",
  "Return",
  "Transfer In",
  "Transfer Out",
  "Reservation",
  "Outward",
  "Revocation",
  "Defect Adjustment",
];

export function StockPositionDetailPage() {
  const { t } = useTranslation();
  const { productId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("position"); // 'position' or 'journal'

  // Product detail states
  const [product, setProduct] = useState(null);
  const [warehouseInventory, setWarehouseInventory] = useState([]);

  // Expanded warehouses tracking
  const [expandedWarehouses, setExpandedWarehouses] = useState({});

  // Journal tab states
  const [journalData, setJournalData] = useState([]);
  const [loadingJournal, setLoadingJournal] = useState(false);
  const [journalFilters, setJournalFilters] = useState({
    warehouse: "All",
    movementType: "All",
    startDate: "",
    endDate: "",
  });
  const [journalPage, setJournalPage] = useState(1);
  const JOURNAL_PAGE_SIZE = 10;

  // Load product metadata and warehouse level inventory
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");

    inventoryManagementService.getStockPositionDetail(productId)
      .then((res) => {
        if (!active) return;
        setProduct(res.product);
        setWarehouseInventory(res.warehouses);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to load product stock details:", err);
        setError(t("validation.productNotFound"));
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [productId, t]);

  // Load scoped journal transactions based on filters
  useEffect(() => {
    if (activeTab !== "journal") return;
    let active = true;
    setLoadingJournal(true);

    inventoryManagementService.getProductJournal(productId, journalFilters)
      .then((res) => {
        if (!active) return;
        setJournalData(res);
        setJournalPage(1); // Reset page to first
        setLoadingJournal(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to filter product journal:", err);
        setLoadingJournal(false);
      });

    return () => {
      active = false;
    };
  }, [activeTab, productId, journalFilters]);

  const toggleWarehouseExpand = (whId) => {
    setExpandedWarehouses((prev) => ({
      ...prev,
      [whId]: !prev[whId],
    }));
  };

  const handleBack = () => {
    navigate("/inventory-management/stock-position");
  };

  // Journal filters handlers
  const handleFilterChange = (key, value) => {
    setJournalFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleResetFilters = () => {
    setJournalFilters({
      warehouse: "All",
      movementType: "All",
      startDate: "",
      endDate: "",
    });
  };

  // Unique warehouses list for journal filter dropdown
  const uniqueJournalWarehouses = useMemo(() => {
    if (warehouseInventory.length > 0) {
      return warehouseInventory.map((w) => w.warehouseName);
    }
    return ["Warehouse Bangalore", "Peelamedu Depot", "Warehouse Mysore", "Warehouse Chennai", "Coimbatore Central Warehouse"];
  }, [warehouseInventory]);

  // Active filters count helper
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (journalFilters.warehouse !== "All") count++;
    if (journalFilters.movementType !== "All") count++;
    if (journalFilters.startDate) count++;
    if (journalFilters.endDate) count++;
    return count;
  }, [journalFilters]);

  // Scoped paginated rows
  const paginatedJournalRows = useMemo(() => {
    const start = (journalPage - 1) * JOURNAL_PAGE_SIZE;
    return journalData.slice(start, start + JOURNAL_PAGE_SIZE);
  }, [journalData, journalPage]);

  const journalTotalPages = Math.max(1, Math.ceil(journalData.length / JOURNAL_PAGE_SIZE));

  const formatJournalDate = (isoStr) => {
    if (!isoStr) return "—";
    const dateObj = new Date(isoStr);
    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = t(MONTH_KEYS[dateObj.getMonth()]);
    const year = dateObj.getFullYear();
    return `${day} ${month} ${year}`;
  };

  // Batch Status Badge Class resolver
  const getBatchStatusBadgeClass = (status) => {
    switch (status) {
      case "Available":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "Low Stock":
        return "bg-amber-50 text-amber-700 border-amber-200";
      case "Reserved":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "Expired Warranty":
        return "bg-rose-50 text-rose-700 border-rose-200";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  // Product status badge color resolver
  const getProductStatusBadgeClass = (status) => {
    switch (status) {
      case "Active":
        return "bg-emerald-50 text-emerald-700 border-emerald-250";
      case "End of Production":
        return "bg-amber-50 text-amber-700 border-amber-250";
      case "Inactive":
        return "bg-gray-50 text-gray-600 border-gray-200";
      default:
        return "bg-gray-50 text-gray-600 border-gray-200";
    }
  };

  return (
    <AppLayout
      breadcrumb={[
        { label: t("inventory.operations") },
        { label: t("inventory.inventoryManagement"), onClick: () => navigate("/inventory-management/stock-position") },
        { label: t("inventory.stockPositionDetail") },
      ]}
      className="space-y-6"
    >
      {/* Custom Title and Back Nav */}
      <div className="space-y-1 font-sans">
        <button
          onClick={handleBack}
          className="inline-flex items-center gap-1 text-[var(--color-primary)] text-xs font-bold hover:underline bg-transparent border-0 cursor-pointer focus:outline-none mb-2"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          <span>{t("formActions.backToStockPosition")}</span>
        </button>

      <PageHeader
        title={t("inventory.stockPositionDetail")}
      />
      </div>

      {loading ? (
        /* Skeletons */
        <div className="space-y-6">
          <ContentCard bodyClassName="p-6 h-[100px] animate-pulse bg-gray-50/20" />
          <ContentCard bodyClassName="p-6 h-[300px] animate-pulse bg-gray-50/20" />
        </div>
      ) : error || !product ? (
        <div className="p-8 text-center text-[var(--color-error)] font-semibold text-sm border bg-white rounded-2xl shadow-sm">
          ⚠️ {error || t("validation.productNotFound")}
        </div>
      ) : (
        <div className="space-y-6 font-sans">
          {/* Card 1: Compact Product Information Card */}
          <div className="rounded-2xl border border-[var(--color-text-border)] bg-white p-5 shadow-sm font-sans">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-4 pb-4 border-b border-gray-100">
              <div className="space-y-0.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-base sm:text-lg font-extrabold text-foreground tracking-tight leading-tight">
                    {product.productName}
                  </h2>
                  <span className={cn(
                    "inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold border",
                    getProductStatusBadgeClass(product.status)
                  )}>
                    {PRODUCT_STATUS_KEYS[product.status] ? t(PRODUCT_STATUS_KEYS[product.status]) : product.status}
                  </span>
                </div>
                <span className="text-xs font-semibold text-[var(--color-text-secondary)] block font-mono">
                  {product.partNumber} · {product.manufacturer}
                </span>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs font-medium">
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 block mb-0.5">
                  {t("common.category")}
                </span>
                <span className="text-foreground font-semibold">{product.category}</span>
              </div>
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 block mb-0.5">
                  {t("uom.uom")}
                </span>
                <span className="text-foreground font-semibold font-mono">{product.uom}</span>
              </div>
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 block mb-0.5">
                  {t("inventory.listPrice")}
                </span>
                <span className="text-foreground font-bold font-mono">
                  {formatCurrency(product.listPrice)}
                </span>
              </div>
              <div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 block mb-0.5">
                  {t("inventory.productIdLabel")}
                </span>
                <span className="text-foreground font-semibold font-mono">{product.productId}</span>
              </div>
            </div>
          </div>

          {/* Tabs Nav bar */}
          <div className="flex border-b border-[var(--color-text-border)] font-sans">
            <button
              onClick={() => setActiveTab("position")}
              className={cn(
                "px-5 py-2.5 font-bold text-xs border-b-2 tracking-wider uppercase transition cursor-pointer focus:outline-none",
                activeTab === "position"
                  ? "border-[var(--color-primary)] text-[var(--color-primary)] bg-transparent"
                  : "border-transparent text-[var(--color-text-secondary)] hover:text-foreground"
              )}
            >
              {t("inventory.stockPosition")}
            </button>
            <button
              onClick={() => setActiveTab("journal")}
              className={cn(
                "px-5 py-2.5 font-bold text-xs border-b-2 tracking-wider uppercase transition cursor-pointer focus:outline-none",
                activeTab === "journal"
                  ? "border-[var(--color-primary)] text-[var(--color-primary)] bg-transparent"
                  : "border-transparent text-[var(--color-text-secondary)] hover:text-foreground"
              )}
            >
              {t("inventory.journal")}
            </button>
          </div>

          {/* TAB 1: Stock Position (Warehouse List + Expandable Batches) */}
          {activeTab === "position" && (
            <ContentCard bodyClassName="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm table-fixed min-w-[1010px] border-collapse font-sans">
                  <colgroup>
                    <col className="w-[250px]" /> {/* Warehouse */}
                    <col className="w-[110px]" /> {/* Total Stock */}
                    <col className="w-[110px]" /> {/* Reserved Stock */}
                    <col className="w-[110px]" /> {/* Maint. Reserve */}
                    <col className="w-[110px]" /> {/* Defective Stock */}
                    <col className="w-[130px] bg-[#EBF2FC]" /> {/* Available Stock */}
                    <col className="w-[130px]" /> {/* Stock Value */}
                    <col className="w-[60px]" /> {/* Expand Arrow */}
                  </colgroup>
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-[var(--color-text-secondary)] bg-[var(--color-workspace)]/30 border-b border-[var(--color-text-border)]/65 font-bold">
                      <th className="px-4 py-3 text-left">{t("inventory.warehouse")}</th>
                      <th className="px-4 py-3 text-right">{t("inventory.total")}</th>
                      <th className="px-4 py-3 text-right">{t("status.reserved")}</th>
                      <th className="px-4 py-3 text-right">{t("inventory.maintReserve")}</th>
                      <th className="px-4 py-3 text-right">{t("inventory.defective")}</th>
                      <th className="px-4 py-3 text-right bg-[#EBF2FC] text-[var(--color-primary)] font-extrabold">{t("status.available")}</th>
                      <th className="px-4 py-3 text-right">{t("inventory.stockValue")}</th>
                      <th className="px-4 py-3 text-center"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 font-medium">
                    {warehouseInventory.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-4 py-8 text-center text-gray-400 font-semibold">
                          {t("inventory.noWarehouseRecords")}
                        </td>
                      </tr>
                    ) : (
                      warehouseInventory.map((wh) => {
                        const isExpanded = !!expandedWarehouses[wh.warehouseId];
                        return (
                          <React.Fragment key={wh.warehouseId}>
                            {/* Main Warehouse Row */}
                            <tr
                              onClick={() => toggleWarehouseExpand(wh.warehouseId)}
                              className="hover:bg-gray-50/70 transition-colors cursor-pointer"
                            >
                              <td className="px-4 py-3.5 font-bold text-foreground truncate">
                                {wh.warehouseName}
                              </td>
                              <td className="px-4 py-3.5 text-right font-semibold text-foreground font-mono">
                                {wh.totalStock.toLocaleString()}
                              </td>
                              <td className="px-4 py-3.5 text-right text-[var(--color-text-secondary)] font-mono">
                                {wh.reservedStock > 0 ? wh.reservedStock.toLocaleString() : "—"}
                              </td>
                              <td className="px-4 py-3.5 text-right text-[var(--color-text-secondary)] font-mono">
                                {wh.maintenanceReserve > 0 ? wh.maintenanceReserve.toLocaleString() : "—"}
                              </td>
                              <td className="px-4 py-3.5 text-right text-rose-600 font-mono">
                                {wh.defectiveStock > 0 ? wh.defectiveStock.toLocaleString() : "—"}
                              </td>
                              <td className="px-4 py-3.5 text-right font-extrabold text-[var(--color-primary)] bg-[#EBF2FC] font-mono">
                                {wh.availableStock.toLocaleString()}
                              </td>
                              <td className="px-4 py-3.5 text-right font-bold text-foreground font-mono">
                                {formatCurrency(wh.stockValue)}
                              </td>
                              <td className="px-4 py-3.5 text-center text-[var(--color-text-secondary)]">
                                {isExpanded ? <ChevronUp className="h-4 w-4 mx-auto" /> : <ChevronDown className="h-4 w-4 mx-auto" />}
                              </td>
                            </tr>

                            {/* Expandable Batches Row */}
                            {isExpanded && (
                              <tr>
                                <td colSpan="8" className="bg-gray-50/50 p-4 border-t border-b border-gray-100">
                                  <div className="space-y-2 pl-4">
                                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--color-primary)] block">
                                      {t("inventory.batchLevelBreakdown")}
                                    </span>
                                    <div className="border border-gray-150 rounded-xl overflow-hidden bg-white shadow-sm overflow-x-auto">
                                      <table className="w-full text-xs table-fixed min-w-[1100px] border-collapse">
                                        <colgroup>
                                          <col className="w-[140px]" /> {/* Batch Number */}
                                          <col className="w-[120px]" /> {/* Receipt Date */}
                                          <col className="w-[120px]" /> {/* Warranty Start */}
                                          <col className="w-[120px]" /> {/* Warranty End */}
                                          <col className="w-[90px]" />  {/* Total */}
                                          <col className="w-[90px]" />  {/* Reserved */}
                                          <col className="w-[90px]" />  {/* Maint. */}
                                          <col className="w-[90px]" />  {/* Defective */}
                                          <col className="w-[110px]" /> {/* Available */}
                                          <col className="w-[130px]" /> {/* Status */}
                                        </colgroup>
                                        <thead>
                                          <tr className="bg-gray-100/70 border-b border-gray-150 text-[10px] uppercase tracking-wide text-gray-400 font-bold">
                                            <th className="px-3 py-2 text-left">{t("inventory.batchNumber")}</th>
                                            <th className="px-3 py-2 text-left">{t("dateTime.receiptDateLabel")}</th>
                                            <th className="px-3 py-2 text-left">{t("inventory.warrantyStart")}</th>
                                            <th className="px-3 py-2 text-left">{t("inventory.warrantyEnd")}</th>
                                            <th className="px-3 py-2 text-right">{t("inventory.total")}</th>
                                            <th className="px-3 py-2 text-right">{t("status.reserved")}</th>
                                            <th className="px-3 py-2 text-right">{t("inventory.maintReserve")}</th>
                                            <th className="px-3 py-2 text-right">{t("inventory.defective")}</th>
                                            <th className="px-3 py-2 text-right text-[var(--color-primary)] font-extrabold">{t("status.available")}</th>
                                            <th className="px-3 py-2 text-center">{t("status.status")}</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 font-medium">
                                          {wh.batches.length === 0 ? (
                                            <tr>
                                              <td colSpan="10" className="px-3 py-4 text-center text-gray-400 font-semibold">
                                                {t("inventory.noStockRecordsFound")}
                                              </td>
                                            </tr>
                                          ) : (
                                            wh.batches.map((b) => (
                                              <tr key={b.batchNumber} className="hover:bg-gray-50/40">
                                                <td className="px-3 py-2 font-mono font-bold text-foreground">
                                                  {b.batchNumber}
                                                </td>
                                                <td className="px-3 py-2 text-gray-500 font-semibold">
                                                  {b.receiptDate ? new Date(b.receiptDate).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                                                </td>
                                                <td className="px-3 py-2 text-gray-500 font-semibold">
                                                  {b.warrantyStart ? new Date(b.warrantyStart).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                                                </td>
                                                <td className="px-3 py-2 text-gray-500 font-semibold">
                                                  {b.warrantyEnd ? new Date(b.warrantyEnd).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—"}
                                                </td>
                                                <td className="px-3 py-2 text-right font-mono text-foreground font-semibold">
                                                  {b.totalQty.toLocaleString()}
                                                </td>
                                                <td className="px-3 py-2 text-right font-mono text-gray-500">
                                                  {b.reservedQty > 0 ? b.reservedQty.toLocaleString() : "—"}
                                                </td>
                                                <td className="px-3 py-2 text-right font-mono text-gray-500">
                                                  {b.maintenanceQty > 0 ? b.maintenanceQty.toLocaleString() : "—"}
                                                </td>
                                                <td className="px-3 py-2 text-right font-mono text-rose-500">
                                                  {b.defectiveQty > 0 ? b.defectiveQty.toLocaleString() : "—"}
                                                </td>
                                                <td className="px-3 py-2 text-right font-mono text-[var(--color-primary)] font-extrabold bg-[#EBF2FC]/30">
                                                  {b.availableQty.toLocaleString()}
                                                </td>
                                                <td className="px-3 py-2 text-center">
                                                  <span className={cn(
                                                    "inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold border",
                                                    getBatchStatusBadgeClass(b.status)
                                                  )}>
                                                    {BATCH_STATUS_KEYS[b.status] ? t(BATCH_STATUS_KEYS[b.status]) : b.status}
                                                  </span>
                                                </td>
                                              </tr>
                                            ))
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </ContentCard>
          )}

          {/* TAB 2: Journal (Chronological History scoped to Product) */}
          {activeTab === "journal" && (
            <div className="space-y-6">
              {/* Scoped Filters Card */}
              <ContentCard title={t("formActions.filters")} bodyClassName="p-5 font-sans">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                  {/* Warehouse Select */}
                  <div className="space-y-1.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                      {t("inventory.warehouse")}
                    </span>
                    <Select
                      value={journalFilters.warehouse}
                      onValueChange={(val) => handleFilterChange("warehouse", val)}
                    >
                      <SelectTrigger className="h-9 rounded-xl border border-[var(--color-text-border)] bg-white text-xs font-semibold focus:ring-[var(--color-accent)] w-full">
                        <SelectValue placeholder={t("inventory.allWarehouses")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="All">{t("inventory.allWarehouses")}</SelectItem>
                        {uniqueJournalWarehouses.map((wName, idx) => (
                          <SelectItem key={idx} value={wName}>
                            {wName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Movement Type Select */}
                  <div className="space-y-1.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                      {t("inventory.movementType")}
                    </span>
                    <Select
                      value={journalFilters.movementType}
                      onValueChange={(val) => handleFilterChange("movementType", val)}
                    >
                      <SelectTrigger className="h-9 rounded-xl border border-[var(--color-text-border)] bg-white text-xs font-semibold focus:ring-[var(--color-accent)] w-full">
                        <SelectValue placeholder={t("inventory.allMovements")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="All">{t("inventory.allMovements")}</SelectItem>
                        {MOVEMENT_FILTER_VALUES.map((type) => (
                          <SelectItem key={type} value={type}>
                            {MOVEMENT_TYPE_KEYS[type] ? t(MOVEMENT_TYPE_KEYS[type]) : type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Date range start */}
                  <div className="space-y-1.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                      {t("dateTime.startDate")}
                    </span>
                    <input
                      type="date"
                      value={journalFilters.startDate}
                      onChange={(e) => handleFilterChange("startDate", e.target.value)}
                      className="w-full h-9 px-3 rounded-xl border border-[var(--color-text-border)] bg-white text-xs font-semibold focus:border-[var(--color-accent)] focus:outline-none"
                    />
                  </div>

                  {/* Date range end */}
                  <div className="space-y-1.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                      {t("dateTime.endDate")}
                    </span>
                    <input
                      type="date"
                      value={journalFilters.endDate}
                      onChange={(e) => handleFilterChange("endDate", e.target.value)}
                      className="w-full h-9 px-3 rounded-xl border border-[var(--color-text-border)] bg-white text-xs font-semibold focus:border-[var(--color-accent)] focus:outline-none"
                    />
                  </div>
                </div>

                {/* Filter footer reset bar */}
                <div className="flex items-center justify-between border-t border-[var(--color-text-border)]/45 mt-4 pt-3.5">
                  <span className="text-[11px] font-semibold text-[var(--color-text-secondary)]">
                    {t("inventory.activeFilters")} <strong className="text-foreground font-mono">{activeFiltersCount}</strong>
                  </span>
                  <button
                    type="button"
                    onClick={handleResetFilters}
                    disabled={activeFiltersCount === 0}
                    className="inline-flex items-center gap-1.5 h-8.5 px-3 rounded-lg border border-[var(--color-text-border)] bg-white text-xs font-bold text-foreground hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition cursor-pointer"
                  >
                    <RefreshCw className="h-3.5 w-3.5 text-[var(--color-text-secondary)]" />
                    <span>{t("formActions.resetFilters")}</span>
                  </button>
                </div>
              </ContentCard>

              {/* Scoped Journal Table Card */}
              <ContentCard bodyClassName="overflow-hidden">
                {loadingJournal ? (
                  <div className="p-6 space-y-4 animate-pulse">
                    <div className="h-10 bg-gray-100 rounded w-full" />
                    <div className="h-10 bg-gray-50 rounded w-full" />
                  </div>
                ) : journalData.length === 0 ? (
                  <div className="p-12 text-center space-y-3 font-sans max-w-sm mx-auto">
                    <div className="h-10 w-10 rounded-full bg-[var(--color-workspace)] text-[var(--color-text-secondary)] grid place-items-center mx-auto">
                      <Funnel className="h-4.5 w-4.5" />
                    </div>
                    <div>
                      <h5 className="text-xs font-bold text-foreground">{t("inventory.noMovementsFound")}</h5>
                      <p className="text-[11px] text-[var(--color-text-secondary)] mt-1.5 leading-normal">
                        {t("inventory.noMovementsFoundHint")}
                      </p>
                    </div>
                    {activeFiltersCount > 0 && (
                      <button
                        type="button"
                        onClick={handleResetFilters}
                        className="inline-flex h-8 px-4 rounded-lg bg-[var(--color-primary)] text-white text-[11px] font-bold hover:bg-[var(--color-accent)] transition cursor-pointer"
                      >
                        {t("formActions.resetFilters")}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm table-fixed min-w-[1120px] border-collapse font-sans">
                        <colgroup>
                          <col className="w-[130px]" /> {/* Date */}
                          <col className="w-[140px]" /> {/* Movement Type */}
                          <col className="w-[200px]" /> {/* Warehouse */}
                          <col className="w-[140px]" /> {/* Batch Number */}
                          <col className="w-[100px]" /> {/* Quantity */}
                          <col className="w-[120px]" /> {/* Running Balance */}
                          <col className="w-[140px]" /> {/* Reference */}
                          <col className="w-[150px]" /> {/* Recorded By */}
                        </colgroup>
                        <thead>
                          <tr className="text-[10px] uppercase tracking-wider text-[var(--color-text-secondary)] bg-[var(--color-workspace)]/30 border-b border-[var(--color-text-border)]/65 font-bold">
                            <th className="px-4 py-3 text-left">{t("dateTime.date")}</th>
                            <th className="px-4 py-3 text-left">{t("inventory.movementType")}</th>
                            <th className="px-4 py-3 text-left">{t("inventory.warehouse")}</th>
                            <th className="px-4 py-3 text-left">{t("inventory.batchNumber")}</th>
                            <th className="px-4 py-3 text-right">{t("inventory.quantity")}</th>
                            <th className="px-4 py-3 text-right">{t("inventory.runningBalance")}</th>
                            <th className="px-4 py-3 text-left">{t("inventory.reference")}</th>
                            <th className="px-4 py-3 text-left">{t("inventory.recordedBy")}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 font-medium">
                          {paginatedJournalRows.map((row) => {
                            const isPositive = row.quantity > 0;
                            return (
                              <tr key={row.id} className="hover:bg-gray-50/50 transition-colors">
                                <td className="px-4 py-3 text-gray-500 font-semibold">
                                  {formatJournalDate(row.date)}
                                </td>
                                <td className="px-4 py-3">
                                  <MovementTypeBadge type={row.movementType} />
                                </td>
                                <td className="px-4 py-3 text-[var(--color-text-secondary)] truncate">
                                  {row.warehouse}
                                </td>
                                <td className="px-4 py-3 text-[var(--color-text-secondary)] font-mono truncate">
                                  {row.batchNumber}
                                </td>
                                <td className={cn(
                                  "px-4 py-3 text-right font-extrabold font-mono",
                                  isPositive ? "text-emerald-600" : "text-rose-600"
                                )}>
                                  {isPositive ? `+${row.quantity}` : row.quantity}
                                </td>
                                <td className="px-4 py-3 text-right font-bold text-foreground font-mono">
                                  {row.runningBalance.toLocaleString()}
                                </td>
                                <td className="px-4 py-3">
                                  <ReferenceLink value={row.reference} />
                                </td>
                                <td className="px-4 py-3 text-gray-500 truncate">
                                  {row.recordedBy}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    <div className="px-4 pb-4">
                      <TablePagination
                        page={journalPage}
                        totalPages={journalTotalPages}
                        totalItems={journalData.length}
                        pageSize={JOURNAL_PAGE_SIZE}
                        onPageChange={setJournalPage}
                      />
                    </div>
                  </div>
                )}
              </ContentCard>
            </div>
          )}
        </div>
      )}
    </AppLayout>
  );
}
